import React, { useEffect, useState } from "react";
import Button from "../Button";
import {
  useAccount,
  useReadContracts,
  useWaitForTransactionReceipt,
  useWatchContractEvent,
  useWriteContract,
} from "wagmi";
import staking_token_abi from "../../hooks/abi/staking_token_abi";
import Reveal from "../Animation/Reveal";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase-config";
import new_staking_abi from "../../hooks/abi/new_staking_abi";
import CountdownTimer from "../CountdownTimer";
import { ethers, toBigInt } from "ethers";
import useShorten from "../../hooks/shorten";
import { LoadingOverlay } from "../LoadingOverlay";

const LockedForm = ({ toggleWalletModal }) => {
  const [targetDocID, setTargetDocID] = useState(null);
  const [targetDocData, setTargetDocData] = useState(null);
  const [action, setAction] = useState(null);
  const [amount, setAmount] = useState("");
  const [showError, setShowError] = useState(false);
  const [fixedStakeData, setFixedStakeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formattedUnlockDate, setFormattedUnlockDate] = useState("");
  const [decodedStakeData, setDecodedStakeData] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const shorten = useShorten();
  const { address } = useAccount();
  const { data: stakeBalance } = useReadContracts({
    contracts: [
      {
        abi: staking_token_abi,
        functionName: "balanceOf",
        address: process.env.REACT_APP_IOTEX_TOKEN_CONTRACT_ADDRESS,
        args: [address],
      },
    ],
  });

  // Approve
  const {
    writeContract: approve,
    data: approveData,
    error: approveError,
  } = useWriteContract();
  // Approve Receipt
  const { data: approveReceipt, status: approveReceiptStatus } =
    useWaitForTransactionReceipt({
      hash: approveData,
      enabled: Boolean(approveData),
    });

  // Stake
  const {
    writeContract: stake,
    data: stakeData,
    error: stakeError,
  } = useWriteContract();
  // Stake Event Listener
  const { data: stakeReceipt } = useWaitForTransactionReceipt({
    hash: stakeData,
    enabled: Boolean(stakeData),
  });

  // Withdraw
  const {
    writeContract: withdraw,
    data: withdrawData,
    error: withdrawError,
    status: withdrawStatus,
  } = useWriteContract();
  // Withdraw Event Listener
  const { data: withdrawReceipt } = useWaitForTransactionReceipt({
    hash: withdrawData,
    enabled: Boolean(withdrawData),
  });

  const textHead = [
    ">_ ",
    <span className="text-[#4284FF]">Staking Mode: </span>,
    "Locked\n",
    ">_ ",
    <span className="text-[#4284FF]">Fixed Locking Duration: </span>,
    "61 Days\n",
    ">_ ",
    <span className="text-[#4284FF]">Annual Percentage Yield (APY): </span>,
    "50.00%\n",
    ">_ ",
    <span className="text-[#4284FF]">Unlock Date: </span>,
    <span>{formattedUnlockDate}</span>,
    "\n",
  ];
  const binobitFormulaText = [
    ">_ Binobit Points = Staked Amount * Days Staked * 0.05\n",
  ];
  const stakePercentage = [
    { title: "25%", value: 0.25 },
    { title: "50%", value: 0.5 },
    { title: "75%", value: 0.75 },
    { title: "Max", value: 1 },
  ];

  // Textbox changes
  const handleChange = (e) => {
    setAmount(e.target.value);
  };
  // Fetching fixed stake data
  const fetchFixedStakeData = async () => {
    try {
      const q = query(
        collection(db, process.env.REACT_APP_DB_COLLECTION),
        where("walletAddress", "in", [
          address,
          address.toLowerCase(),
          address.toUpperCase(),
        ]),
        where("stakeType", "==", 0),
        orderBy("stakeDate", "desc")
      );

      let querySnapshot;
      querySnapshot = await getDocs(q);

      let finalData = [];

      querySnapshot.forEach((doc) => {
        const docId = doc.id;
        const docData = doc.data();

        const concatData = {
          ...docData,
          id: docId,
        };

        finalData.push(concatData);
      });

      setFixedStakeData(finalData);
    } catch (e) {
      console.error("Error fetching document: ", e);
      console.log("No data found");
    }
  };
  // Decode Hex Data from stakeReceipt
  const decodeHexData = (hexData) => {
    // Ensure the input starts with '0x'
    if (!hexData.startsWith("0x")) {
      throw new Error("Hex data must start with '0x'");
    }

    // Split the hex data into 32-byte (64-character) chunks
    const chunkSize = 64;
    const chunks = [];
    for (let i = 2; i < hexData.length; i += chunkSize) {
      chunks.push(hexData.slice(i, i + chunkSize));
    }

    let decodedData = [];

    // Decode each chunk using ethers BigNumber
    decodedData = chunks.map((chunk, index) => ({
      chunk: index + 1,
      hex: `0x${chunk}`,
      decimal: ethers.getBigInt(`0x${chunk}`).toString(),
    }));

    setDecodedStakeData(decodedData);
  };

  // Run stake function through approve
  const lockStake = () => {
    if (amount !== "") {
      setShowError(false);
      stakeToken();
    } else {
      setShowError(true);
    }
  };
  // Stake token function
  const stakeToken = async () => {
    setLoading(true);
    try {
      approve({
        abi: new_staking_abi,
        address: process.env.REACT_APP_IOTEX_TOKEN_CONTRACT_ADDRESS,
        functionName: "approve",
        args: [
          process.env.REACT_APP_DEPINS_STAKING_CONTRACT_ADDRESS,
          process.env.REACT_APP_IOTEX_TOKEN_CONTRACT_ADDRESS,
        ],
      });
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };
  // Submit Stake Data (ON STAKE SUCCESS)
  const submitStakeData = async () => {
    if (decodedStakeData) {
      try {
        await addDoc(collection(db, process.env.REACT_APP_DB_COLLECTION), {
          binobitEarned: null,
          claimAmount: null,
          claimDate: null,
          claimHash: null,
          depinsEarned: null,
          stakeAmount: ethers
            .formatEther(decodedStakeData[4].decimal)
            .toString(),
          stakeDate: new Date(),
          stakeHash: stakeData,
          stakeType: Number(decodedStakeData[1].decimal),
          tokenId: Number(decodedStakeData[0].decimal),
          walletAddress: address.toLowerCase(),
        });
        setLoading(false);
        setFormSubmitted(true);
      } catch (e) {
        console.error("Error adding document: ", e);
      }
    }
  };
  //Withdraw Token function
  const withdrawToken = async () => {
    setLoading(true);
    try {
      if (fixedStakeData !== null) {
        withdraw({
          abi: new_staking_abi,
          address: process.env.REACT_APP_DEPINS_STAKING_CONTRACT_ADDRESS,
          functionName: "withdraw",
          args: [targetDocData.tokenId],
        });
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };
  // Update Claim Data
  const updateClaimData = async () => {
    try {
      const docRef = doc(db, process.env.REACT_APP_DB_COLLECTION, targetDocID);
      await updateDoc(docRef, {
        claimAmount: targetDocData.stakeAmount,
        claimDate: new Date(),
        claimHash: withdrawData,
        depinsEarned: (
          ((Number(targetDocData.stakeAmount) * 0.5) / 365) *
          61
        ).toFixed(6),
        binobitEarned: (
          Number(targetDocData.stakeAmount) *
          targetDocData.daysStaked *
          0.05
        ).toFixed(6),
      });
      console.log("Document updated successfully");
      setLoading(false);
      setFormSubmitted(true);
    } catch (e) {
      console.error("Error updating document: ", e);
    }
  };

  useEffect(() => {
    if (approveReceipt && approveReceiptStatus === "success") {
      stake({
        abi: new_staking_abi,
        address: process.env.REACT_APP_DEPINS_STAKING_CONTRACT_ADDRESS,
        functionName: "stake",
        args: [0, ethers.parseEther(amount.toString())],
      });
    }
  }, [approveReceipt]);

  // Decode Hex Data from stakeReceipt
  useEffect(() => {
    if (stakeReceipt) {
      decodeHexData(stakeReceipt.logs[3].data);
    }
  }, [stakeReceipt]);
  // Submit Stake Data (ON STAKE SUCCESS)
  useEffect(() => {
    if (decodedStakeData) {
      submitStakeData();
    }
  }, [decodedStakeData]);

  useEffect(() => {
    if (withdrawReceipt) {
      updateClaimData();
    }
  }, [withdrawReceipt]);

  useEffect(() => {
    if (withdrawError || approveError || stakeError) {
      setLoading(false);
      window.location.reload();
    }
  }, [withdrawError, approveError, stakeError]);

  useEffect(() => {
    fetchFixedStakeData();
    setFormattedUnlockDate(
      new Date(new Date().getTime() + 61 * 24 * 60 * 60 * 1000).toDateString()
    );
  }, []);
  useEffect(() => {
    setTargetDocData(fixedStakeData?.find((data) => data.id === targetDocID));
  }, [targetDocID]);
  useEffect(() => {
    if (action && targetDocData) {
      if (action === "withdraw") {
        withdrawToken();
      }
    }
  }, [action, targetDocData]);
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (loading) {
        event.preventDefault();
        event.returnValue = "Changes you made may not be saved.";
      }
    };

    if (loading) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [loading]);
  useEffect(() => {
    if (formSubmitted) {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [formSubmitted]);

  return (
    <Reveal className="flex flex-col w-full gap-[64px] items-center">
      {loading && <LoadingOverlay />}
      <div className="border-2 border-[#4284FF] flex flex-col w-full max-w-[704px] p-[32px] bg-black drop-shadow-[0_0_12px_rgba(0,88,255,32)] gap-[24px]">
        <div className="whitespace-pre-wrap">
          {textHead.map((part, index) => (
            <React.Fragment key={index}>{part}</React.Fragment>
          ))}
        </div>
        <div className="flex flex-col w-full gap-[8px]">
          <p>{">_ Add $DePINs"}</p>
          <div className="flex w-full border border-white p-[12px]">
            <input
              type="text"
              name="amount"
              value={amount}
              disabled={!address}
              className="w-full text-white bg-black outline-none placeholder:opacity-20"
              placeholder="0.00"
              onChange={handleChange}
            />
            <img
              src="/images/depins-round-icon.svg"
              alt="depins-icon"
              className="w-[24px] h-[24px]"
            />
          </div>
          <div className="flex w-full gap-[8px] overflow-x-auto no-scrollbar">
            {stakePercentage.map((percentage, index) => (
              <Button
                key={index}
                variant="tertiary"
                className="border border-[#4284FF] bg-black w-full"
                disabled={!address}
                onClick={() => {
                  setAmount(
                    stakeBalance[0].result
                      ? Number(ethers.formatEther(stakeBalance[0].result)) *
                          percentage.value
                      : 0
                  );
                }}
              >
                {percentage.title}
              </Button>
            ))}
          </div>
          {stakeBalance && (
            <div className="flex w-full justify-between items-center">
              <p>Balance:</p>
              <p>
                <span className="text-[#4284FF]">
                  {stakeBalance[0].result
                    ? Number(ethers.formatEther(stakeBalance[0].result))
                    : 0}
                </span>{" "}
                $DePINs
              </p>
            </div>
          )}
          {showError && (
            <p className="text-[#FF0000]">Please key in an amount to stake.</p>
          )}
        </div>
        {/* <div className="whitespace-pre-wrap">
          {binobitFormulaText.map((part, index) => (
            <React.Fragment key={index}>{part}</React.Fragment>
          ))}
        </div> */}
        {address ? (
          <Button
            variant="primary"
            colour="blue"
            onClick={lockStake}
            className="w-full"
          >
            Stake
          </Button>
        ) : (
          <Button
            variant="primary"
            colour="blue"
            onClick={toggleWalletModal}
            className="w-full"
          >
            Connect Wallet
          </Button>
        )}
      </div>

      {address && (
        <div className="flex flex-col w-full gap-[24px] lg:max-w-[80%]">
          <h2>Your $DePINs Stakes</h2>
          <div className="border-2 border-[#4284FF] flex flex-col w-full bg-black drop-shadow-[0_0_12px_rgba(0,88,255,32)] gap-[24px] overflow-x-auto">
            <table>
              <thead>
                <tr className="border-b border-[#FFFFFF33] text-[#FFFFFFCC] text-nowrap">
                  <th className="font-normal py-[12px] px-[16px] text-start">
                    Stake Hash
                  </th>
                  <th className="font-normal py-[12px] px-[16px] text-start">
                    APY
                  </th>
                  <th className="font-normal py-[12px] px-[16px] text-start">
                    Staked Amount
                  </th>
                  {/* <th className="font-normal py-[12px] px-[16px] text-start">
                      DePINs Earned
                    </th>
                    <th className="font-normal py-[12px] px-[16px] text-start">
                      Binobit Earned
                    </th> */}
                  <th className="font-normal py-[12px] px-[16px] text-start">
                    Stake Date
                  </th>
                  <th className="font-normal py-[12px] px-[16px] text-start">
                    Withdraw Cooldown
                  </th>
                  <th className="font-normal py-[12px] px-[16px] text-start"></th>
                </tr>
              </thead>
              {fixedStakeData &&
              (fixedStakeData === null || fixedStakeData.length === 0) ? (
                <tbody>
                  <tr>
                    <td className="py-[12px] px-[16px] text-center" colSpan="8">
                      No data found
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {fixedStakeData &&
                    fixedStakeData.map((data, index) => (
                      <tr key={index}>
                        <td className="py-[12px] px-[16px]">
                          {data.stakeHash ? (
                            <a
                              // href={`https://testnet.iotexscan.io/tx/${data.stakeHash}`}
                              href={`https://iotexscan.io/tx/${data.stakeHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {shorten(data.stakeHash, 5, 3)}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="py-[12px] px-[16px]">50%</td>
                        <td className="py-[12px] px-[16px]">
                          {Number(data.stakeAmount) ?? "-"}
                        </td>
                        {/* <td className="py-[12px] px-[16px]">
                            {Number(ethers.formatEther(data.stakeAmount)) ??
                              "-"}
                          </td>
                          <td className="py-[12px] px-[16px]">
                            {Number(ethers.formatEther(data.stakeAmount)) *
                              61 *
                              0.05 ?? "-"}
                          </td> */}
                        <td className="py-[12px] px-[16px]">
                          {data.stakedDate !== null
                            ? new Date(
                                data.stakeDate.seconds * 1000
                              ).toLocaleDateString("en-UK")
                            : "-"}
                        </td>
                        <td className="py-[12px] px-[16px]">
                          {data.stakeDate !== null ? (
                            <CountdownTimer
                              date={
                                new Date(
                                  data.stakeDate.seconds * 1000 +
                                    61 * 24 * 60 * 60 * 1000
                                )
                              }
                              completedText="-"
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="py-[12px] px-[16px]">
                          {data.stakeDate !== null &&
                          new Date(
                            data.stakeDate.seconds * 1000 +
                              61 * 24 * 60 * 60 * 1000
                          ) < new Date() ? (
                            <Button
                              variant="tertiary"
                              className="border border-[#4284FF] bg-black w-full"
                              onClick={() => {
                                setTargetDocID(data.id);
                                setAction("withdraw");
                              }}
                            >
                              Withdraw
                            </Button>
                          ) : (
                            <Button
                              variant="tertiary"
                              className="border border-[#4284FF] bg-black w-full"
                              disabled
                            >
                              Locked
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              )}
            </table>
          </div>
        </div>
      )}
    </Reveal>
  );
};

export default LockedForm;
